import Axios, { AxiosResponse } from 'axios';
import PropTypes from 'prop-types';
import { Action } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { ConfigBase } from '../../types/config';
import { Utils } from '../../utils';

import { AdyenCardDTO } from '../../types/dto/AdyenCardDTO';
import { ChargebeeCardDetailsDTO } from '../../types/dto/ChargebeeCardDetailsDTO';
import { ChargebeeCouponDTO } from '../../types/dto/ChargebeeCouponDTO';
import { ChargebeeCreateSubscriptionDTO } from '../../types/dto/ChargebeeCreateSubscriptionDTO';
import { ChargebeePlanDTO } from '../../types/dto/ChargebeePlanDTO';
import { ChargebeeSubscriptionDTO } from '../../types/dto/ChargebeeSubscriptionDTO';
import { SubscriptionCreditNoteDTO } from '../../types/dto/SubscriptionCreditNoteDTO';
import { SubscriptionInvoiceDTO } from '../../types/dto/SubscriptionInvoiceDTO';
import { SubscriptionItemDTO } from '../../types/dto/SubscriptionItemDTO';
import ApiHelper from '../ApiHelper';
import { AuthState } from '../auth/Types';
import * as Actions from './Actions';
import { SubscriptionError, SubscriptionErrorInfo, SubscriptionState } from './Types';

type RootSubState = {
    authState: AuthState,
    subscriptionState: SubscriptionState,
};

type CommonAppThunkAction<R> = ThunkAction<R, RootSubState, undefined, Action>;
type CommonAppThunkDispatch = ThunkDispatch<RootSubState, undefined, Action>;

export type ServiceActions = {
    getActivePlans: () => Promise<ChargebeePlanDTO[]>,
    getSubscriptionItems: () => Promise<SubscriptionItemDTO[]>,
    getUserSubscriptions: () => Promise<ChargebeeSubscriptionDTO[]>,
    getPrimaryPaymentSource: () => Promise<ChargebeeCardDetailsDTO>,
    getUserSubscriptionInvoices: () => Promise<SubscriptionInvoiceDTO[]>,
    getUserSubscriptionCreditNotes: () => Promise<SubscriptionCreditNoteDTO[]>,
    updatePaymentSource: (model: AdyenCardDTO) => Promise<boolean>,
    getCouponDetails: (couponCode: string) => Promise<ChargebeeCouponDTO>,
    updateVehicleFromDOM: (vehCode: number) => Promise<void>,
    createSubscriptionV2: (model: ChargebeeCreateSubscriptionDTO) => Promise<ChargebeeSubscriptionDTO>,
    cancelSubscription: (model: ChargebeeSubscriptionDTO) => Promise<ChargebeeSubscriptionDTO>,
    printSubscriptionInvoice: (invoiceCode: number) => Promise<Blob>,
    printSubscriptionCreditNote: (creditNoteCode: number) => Promise<Blob>,
    getAdyenPaymentMethods: () => Promise<any>,
};

export const ServiceActionPropTypes = PropTypes.shape({
    getActivePlans: PropTypes.func.isRequired,
    getSubscriptionItems: PropTypes.func.isRequired,
    getUserSubscriptions: PropTypes.func.isRequired,
    getPrimaryPaymentSource: PropTypes.func.isRequired,
    getUserSubscriptionInvoices: PropTypes.func.isRequired,
    getUserSubscriptionCreditNotes: PropTypes.func.isRequired,
    updatePaymentSource: PropTypes.func.isRequired,
    getCouponDetails: PropTypes.func.isRequired,
    updateVehicleFromDOM: PropTypes.func.isRequired,
    createSubscriptionV2: PropTypes.func.isRequired,
    cancelSubscription: PropTypes.func.isRequired,
    printSubscriptionInvoice: PropTypes.func.isRequired,
    printSubscriptionCreditNote: PropTypes.func.isRequired,
    getAdyenPaymentMethods: PropTypes.func.isRequired,
});

export const createServiceActions = (config: ConfigBase) => {
    const apiHelper = ApiHelper(config);

    const getActivePlans = (): CommonAppThunkAction<Promise<ChargebeePlanDTO[]>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.getActivePlansRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/subscription/getActivePlans/`, null));
                const response = axiosResponse.data as ChargebeePlanDTO[];
                dispatch(Actions.getActivePlansSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.getActivePlansFailure());
                throw error;
            }
        };
    };

    const getSubscriptionItems = (): CommonAppThunkAction<Promise<SubscriptionItemDTO[]>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.getSubscriptionItemsRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/subscription/getsubscriptionitems/`, null));
                const response = axiosResponse.data as SubscriptionItemDTO[];
                dispatch(Actions.getSubscriptionItemsSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.getSubscriptionItemsFailure());
                throw error;
            }
        };
    };

    const getUserSubscriptions = (): CommonAppThunkAction<Promise<ChargebeeSubscriptionDTO[]>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.getUserSubscriptionsRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/subscription/getUserSubscriptions`, null));
                const response = axiosResponse.data as ChargebeeSubscriptionDTO[];
                dispatch(Actions.getUserSubscriptionsSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.getUserSubscriptionsFailure());
                throw error;
            }
        };
    };

    const getPrimaryPaymentSource = (): CommonAppThunkAction<Promise<ChargebeeCardDetailsDTO>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.getPrimaryPaymentSourceRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/subscription/getPrimaryPaymentSource`, null));
                const response = axiosResponse.data as ChargebeeCardDetailsDTO;
                dispatch(Actions.getPrimaryPaymentSourceSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.getPrimaryPaymentSourceFailure());
                throw error;
            }
        };
    };

    const getUserSubscriptionInvoices = (): CommonAppThunkAction<Promise<SubscriptionInvoiceDTO[]>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.getUserSubscriptionInvoicesRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/subscription/getUserSubscriptionInvoices`, null));
                const response = axiosResponse.data as SubscriptionInvoiceDTO[];
                dispatch(Actions.getUserSubscriptionInvoicesSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.getUserSubscriptionInvoicesFailure());
                throw error;
            }
        };
    };

    const getUserSubscriptionCreditNotes = (): CommonAppThunkAction<Promise<SubscriptionCreditNoteDTO[]>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.getUserSubscriptionCreditNotesRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/subscription/getUserSubscriptionCreditNotes`, null));
                const response = axiosResponse.data as SubscriptionCreditNoteDTO[];
                dispatch(Actions.getUserSubscriptionCreditNotesSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.getUserSubscriptionCreditNotesFailure());
                throw error;
            }
        };
    };

    const updatePaymentSource = (model: AdyenCardDTO): CommonAppThunkAction<Promise<boolean>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.updatePaymentSourceRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.post(`/api/${config.ResourceCode}/subscription/updatePaymentSource`, model));
                const response = axiosResponse.data as boolean;
                dispatch(Actions.updatePaymentSourceSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.updatePaymentSourceFailure());
                throw error;
            }
        };
    };

    const getCouponDetails = (couponCode: string): CommonAppThunkAction<Promise<ChargebeeCouponDTO>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.getCouponDetailsRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/subscription/getCouponDetails?couponCode=${couponCode}`, null));
                const response = axiosResponse.data as ChargebeeCouponDTO;
                dispatch(Actions.getCouponDetailsSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.getCouponDetailsFailure());
                throw error;
            }
        };
    };

    const updateVehicleFromDOM = (vehCode: number): CommonAppThunkAction<Promise<void>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.updateVehicleFromDOMRequest());
                const response: AxiosResponse = await dispatch(apiHelper.post(`/api/${config.ResourceCode}/subscription/updateVehicleFromDOM`, vehCode ));
                Utils.checkHttpStatus(response);
                dispatch(Actions.updateVehicleFromDOMSuccess());
            } catch (error) {
                dispatch(Actions.updateVehicleFromDOMFailure());
                throw error;
            }
        };
    };

    const createSubscriptionV2 = (model: ChargebeeCreateSubscriptionDTO): CommonAppThunkAction<Promise<ChargebeeSubscriptionDTO>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.createSubscriptionV2Request());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.post(`/api/${config.ResourceCode}/subscription/createSubscriptionV2`, model));
                const response = axiosResponse.data as ChargebeeSubscriptionDTO;
                dispatch(Actions.createSubscriptionV2Success(response));
                return response;
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error));
                try {
                    const anyError = error as any;
                    const subscriptionErrorInfo: SubscriptionErrorInfo = {
                        response: anyError?.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                    };

                    subscriptionErrorInfo.errorCode = config.Constants.ErrorCodes.UnknownError;
                    if (Axios.isAxiosError(error) && error.response) {
                        subscriptionErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                        subscriptionErrorInfo.exceptionType = error.response.data.ExceptionType;
                    } else {
                        subscriptionErrorInfo.errorMessage = anyError?.message;
                    }
                    dispatch(Actions.createSubscriptionV2Failure(subscriptionErrorInfo));
                    throw new SubscriptionError(subscriptionErrorInfo);
                } catch (subError) {
                    if (subError instanceof SubscriptionError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };

    const cancelSubscription = (model: ChargebeeSubscriptionDTO): CommonAppThunkAction<Promise<ChargebeeSubscriptionDTO>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.cancelSubscriptionRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.post(`/api/${config.ResourceCode}/subscription/cancelsubscription`, model));
                const response = axiosResponse.data as ChargebeeSubscriptionDTO;
                dispatch(Actions.cancelSubscriptionSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.cancelSubscriptionFailure());
                throw error;
            }
        };
    };

    const printSubscriptionInvoice = (invoiceCode: number): CommonAppThunkAction<Promise<Blob>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.printSubscriptionInvoiceRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/subscription/printsubscriptioninvoice?invoiceCode=${invoiceCode}`, null));
                const response = axiosResponse.data as Blob;
                dispatch(Actions.printSubscriptionInvoiceSuccess(response));
                return response;
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error));
                try {
                    const anyError = error as any;
                    const subscriptionErrorInfo: SubscriptionErrorInfo = {
                        response: anyError?.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                    };

                    subscriptionErrorInfo.errorCode = config.Constants.ErrorCodes.UnknownError;
                    if (Axios.isAxiosError(error) && error.response) {
                        subscriptionErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                        subscriptionErrorInfo.exceptionType = error.response.data.ExceptionType;
                    } else {
                        subscriptionErrorInfo.errorMessage = anyError?.message;
                    }
                    dispatch(Actions.printSubscriptionInvoiceFailure(subscriptionErrorInfo));
                    throw new SubscriptionError(subscriptionErrorInfo);
                } catch (subError) {
                    if (subError instanceof SubscriptionError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };

    const printSubscriptionCreditNote = (creditNoteCode: number): CommonAppThunkAction<Promise<Blob>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.printSubscriptionCreditNoteRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/subscription/printsubscriptioncreditnote?creditNoteCode=${creditNoteCode}`, null));
                const response = axiosResponse.data as Blob;
                dispatch(Actions.printSubscriptionCreditNoteSuccess(response));
                return response;
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error));
                try {
                    const anyError = error as any;
                    const subscriptionErrorInfo: SubscriptionErrorInfo = {
                        response: anyError?.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                    };

                    subscriptionErrorInfo.errorCode = config.Constants.ErrorCodes.UnknownError;
                    if (Axios.isAxiosError(error) && error.response) {
                        subscriptionErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                        subscriptionErrorInfo.exceptionType = error.response.data.ExceptionType;
                    } else {
                        subscriptionErrorInfo.errorMessage = anyError?.message;
                    }
                    dispatch(Actions.printSubscriptionCreditNoteFailure(subscriptionErrorInfo));
                    throw new SubscriptionError(subscriptionErrorInfo);
                } catch (subError) {
                    if (subError instanceof SubscriptionError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };

    const getAdyenPaymentMethods = (): CommonAppThunkAction<Promise<any>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.getAdyenPaymentMethodsRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/subscription/getAdyenPaymentMethods`, null));
                const response = axiosResponse.data as any;
                dispatch(Actions.getAdyenPaymentMethodsSuccess(response));
                return response;
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error));
                try {
                    const anyError = error as any;
                    const subscriptionErrorInfo: SubscriptionErrorInfo = {
                        response: anyError?.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                    };

                    subscriptionErrorInfo.errorCode = config.Constants.ErrorCodes.UnknownError;
                    if (Axios.isAxiosError(error) && error.response) {
                        subscriptionErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                        subscriptionErrorInfo.exceptionType = error.response.data.ExceptionType;
                    } else {
                        subscriptionErrorInfo.errorMessage = anyError?.message;
                    }
                    dispatch(Actions.getAdyenPaymentMethodsFailure(subscriptionErrorInfo));
                    throw new SubscriptionError(subscriptionErrorInfo);
                } catch (subError) {
                    if (subError instanceof SubscriptionError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };

    return {
        getActivePlans,
        getSubscriptionItems,
        getUserSubscriptions,
        getPrimaryPaymentSource,
        getUserSubscriptionInvoices,
        getUserSubscriptionCreditNotes,
        updatePaymentSource,
        getCouponDetails,
        updateVehicleFromDOM,
        createSubscriptionV2,
        cancelSubscription,
        printSubscriptionInvoice,
        printSubscriptionCreditNote,
        getAdyenPaymentMethods,
    };
};

export const bindServiceActionCreators = (config: ConfigBase, dispatch: CommonAppThunkDispatch): ServiceActions => {
    const services = createServiceActions(config);
    return {
        getActivePlans: () => dispatch(services.getActivePlans()),
        getSubscriptionItems: () => dispatch(services.getSubscriptionItems()),
        getUserSubscriptions: () => dispatch(services.getUserSubscriptions()),
        getPrimaryPaymentSource: () => dispatch(services.getPrimaryPaymentSource()),
        getUserSubscriptionInvoices: () => dispatch(services.getUserSubscriptionInvoices()),
        getUserSubscriptionCreditNotes: () => dispatch(services.getUserSubscriptionCreditNotes()),
        updatePaymentSource: (model: AdyenCardDTO) => dispatch(services.updatePaymentSource(model)),
        getCouponDetails: (couponCode: string) => dispatch(services.getCouponDetails(couponCode)),
        updateVehicleFromDOM: (vehCode: number) => dispatch(services.updateVehicleFromDOM(vehCode)),
        createSubscriptionV2: (model: ChargebeeCreateSubscriptionDTO) => dispatch(services.createSubscriptionV2(model)),
        cancelSubscription: (model: ChargebeeSubscriptionDTO) => dispatch(services.cancelSubscription(model)),
        printSubscriptionInvoice: (invoiceCode: number) => dispatch(services.printSubscriptionInvoice(invoiceCode)),
        printSubscriptionCreditNote: (creditNoteCode: number) => dispatch(services.printSubscriptionCreditNote(creditNoteCode)),
        getAdyenPaymentMethods: () => dispatch(services.getAdyenPaymentMethods()),
    };
};
